import React from 'react';
import classNames from 'classnames/bind';
import { Pagination } from '@mantine/core';
import { CellProps, Column } from 'react-table';
import { useNavigate } from 'react-router-dom';

import { Camera } from 'types/camera';
import { Stream } from 'types/stream';

import { relativeTimeFromNow } from 'services/date';
import { isNotNullOrUndefined } from 'services/nullable';
import { pluralize } from 'services/string';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDeleteCameras } from 'cameras/hooks/useDeleteCameras';
import { useGateways } from 'gateways/hooks/useGateways';
import { useAPI } from 'hooks/api/useAPI';
import { useAppRedirect } from 'hooks/useAppRedirect';
import { useHasAccess } from 'hooks/useHasAccess';
import { useModal } from 'hooks/useModal';
import { useMqttStreamsState } from 'streams/hooks/useMqttStreamsState';
import { useStreamsByCameras } from 'streams/hooks/useStreamsByCameras';
import { useUpdateCamera } from 'cameras/hooks/useUpdateCamera';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { Loader } from 'components/Loader/Loader';
import { NameCell, TableLabelsCell } from 'components/Table/Table';
import {
  PaginatedTable,
  PaginatedTableProps,
} from 'components/Table/PaginatedTable';
import { TableBulkActionType } from 'components/Table/components/BulkActions/BulkActions';
import { Text } from 'components/Text/Text';
import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';
import { ViewMode } from 'components/ViewModeSwitch/ViewModeSwitch';

import { CamerasTableActionsCell } from './ActionsCell';
import { CamerasTableGatewayCell } from './GatewayCell';
import { CamerasTableStatusCell } from './StatusCell';
import styles from './Table.module.scss';

const c = classNames.bind(styles);

const isH264 = (stream: Stream) =>
  stream.name.toLowerCase().includes('h264') ||
  stream.name.toLowerCase().includes('h.264') ||
  stream.source === 'pipeline_stream';

export type CamerasTableProps = Pick<
  PaginatedTableProps<Camera>,
  'queryResult' | 'pageSize' | 'page' | 'onPageChange'
> & {
  hasFilters: boolean;
  onCreate: () => void;
  viewMode: ViewMode;
};

function CamerasTable({
  hasFilters,
  onCreate,
  viewMode,
  ...props
}: CamerasTableProps) {
  const navigate = useNavigate();
  const { applicationID } = useAPI();
  const redirect = useAppRedirect();

  const { data: currentPlan, isSuccess: isPlanSuccess } = useCurrentPlan();
  const [hasAccess] = useHasAccess();
  const { open } = useModal();

  const canCreateTags =
    (isPlanSuccess && currentPlan === null) ||
    Boolean(currentPlan?.can_create_tags);

  const gatewayIDs = [
    ...new Set(
      props.queryResult?.data?.data
        .map(({ gateway_id }) => gateway_id)
        .filter(isNotNullOrUndefined)
    ),
  ];

  const { data: gateways } = useGateways({
    params: {
      gateway_ids: gatewayIDs,
    },
  });

  const [isRenamingCamera, setIsRenamingCamera] = React.useState<
    Camera['id'] | null
  >(null);

  const { mutateAsync: deleteCameras, isLoading } = useDeleteCameras();
  const { mutate: updateCamera } = useUpdateCamera();

  const nonVirtualCameras = React.useMemo(() => {
    const cameras = props.queryResult.data?.data ?? [];
    return cameras.filter((camera) => camera.conn_type !== 'virtual');
  }, [props.queryResult.data?.data]);

  const { streams: allStreams } = useStreamsByCameras({
    cameras: nonVirtualCameras,
    sources: ['camera_stream', 'uri_stream', 'pipeline_stream'],
    stream_types: ['webrtc', 'rtsp'],
    limit: 50,
    enabled: nonVirtualCameras.length > 0,
  });

  useMqttStreamsState(allStreams.map(({ id }) => id));

  const getStreamForCamera = React.useCallback(
    (cameraId: string) => {
      if (allStreams.length === 0) {
        return null;
      }

      const cameraStreams = allStreams.filter(
        (stream) => stream.camera_id === cameraId
      );

      if (cameraStreams.length === 0) {
        return null;
      }

      const h264Streams = cameraStreams.filter(isH264);
      return h264Streams[0] || cameraStreams[0];
    },
    [allStreams]
  );

  const columns = React.useMemo(() => {
    const columns: Column<Camera>[] = [
      {
        Header: 'Camera',
        Cell(props: CellProps<Camera>) {
          function handleNameChange(newName: string) {
            updateCamera({ id: props.row.original.id, name: newName });
            setIsRenamingCamera(null);
          }

          function cancelRename() {
            setIsRenamingCamera(null);
          }

          const stream = getStreamForCamera(props.row.original.id);

          return (
            <div className={c('camera')}>
              <VideoSourceSnapshot
                className={c('camera-thumbnail')}
                source={stream || props.row.original}
                camera={props.row.original}
                compact
                hideTitle
                autoRefresh={false}
              />
              <div className={c('camera-summary')}>
                <NameCell
                  {...props}
                  to={`/deploy/cameras/${props.row.original.id}`}
                  isRenaming={isRenamingCamera === props.row.original.id}
                  onNameChange={handleNameChange}
                  onCancel={cancelRename}
                  entity="camera"
                />
              </div>
            </div>
          );
        },
      },
      { Header: 'State', Cell: CamerasTableStatusCell },
      { Header: 'Model', accessor: (camera: Camera) => camera.model },
      {
        Header: 'Gateway',
        Cell(props: CellProps<Camera>) {
          const { gateway_id } = props.row.original;

          const gateway = gateway_id
            ? gateways?.data.find(({ id }) => id === gateway_id)
            : undefined;

          return <CamerasTableGatewayCell {...props} gateway={gateway} />;
        },
      },
      { Header: 'Address', accessor: (camera: Camera) => camera.ip_local },
    ];

    if (canCreateTags) {
      columns.push({
        Header: 'Labels',
        Cell(props: CellProps<Camera>) {
          return (
            <TableLabelsCell
              {...props}
              entity="cameras"
              listQueryKey={['cameras']}
              singleQueryKey={['camera']}
              readOnly={!hasAccess('deploy_edit')}
            />
          );
        },
      });
    }

    columns.push({
      id: 'updated_at',
      Header: 'Recent Activity',
      accessor: (camera: Camera) => relativeTimeFromNow(camera.updated_at),
      sortType: 'date',
    });

    if (hasAccess('deploy_edit')) {
      columns.push({
        Header: ' ',
        Cell(props: CellProps<Camera>) {
          return (
            <CamerasTableActionsCell
              {...props}
              onRenameClick={setIsRenamingCamera}
            />
          );
        },
      });
    }

    return columns;
  }, [
    gateways,
    isRenamingCamera,
    canCreateTags,
    hasAccess,
    updateCamera,
    getStreamForCamera,
  ]);

  const bulkActions = React.useMemo(() => {
    if (hasAccess('deploy_edit')) {
      return [
        {
          action: 'Delete',
          intent: 'danger',
          onClick: (ids) => {
            open('confirm-delete', {
              title: `Are you sure you want to delete ${pluralize(ids.length, 'this', String(ids.length))} ${pluralize(ids.length, 'camera')}?`,
              onConfirm: () => deleteCameras({ camera_ids: ids }),
            });
          },
          loading: isLoading,
        },
      ] as TableBulkActionType[];
    }
  }, [hasAccess, isLoading, open, deleteCameras]);

  const emptyMessage = hasFilters ? (
    <EmptyView title="No cameras matching the filter criteria found.">
      Please try another filter combination or clear filters.
    </EmptyView>
  ) : (
    <EmptyView
      title="You have not added any cameras yet."
      buttonText="Add camera"
      onButtonClick={onCreate}
    />
  );

  const { isInitialLoading, isSuccess, error, data } = props.queryResult;

  function navigateToCamera(camera: Camera, openInNewTab = false) {
    const to = `/deploy/cameras/${camera.id}`;

    if (openInNewTab) {
      window.open(`/applications/${applicationID}${to}`, '_blank');
      return;
    }

    redirect(to);
  }

  function handleGridItemNavigate(
    event: React.MouseEvent<HTMLElement>,
    camera: Camera
  ) {
    if (
      event.target !== event.currentTarget &&
      (['input', 'label', 'a', 'button'].includes(
        (event.target as Element).localName
      ) ||
        !event.currentTarget.contains(event.target as HTMLElement))
    ) {
      return;
    }

    navigateToCamera(camera, event.metaKey);
  }

  if (viewMode === 'grid') {
    if (isInitialLoading) {
      return (
        <div className={c('loader')}>
          <Loader size="small" text="Loading..." />
        </div>
      );
    }

    if (isSuccess && data?.total_elements === 0) {
      return emptyMessage;
    }

    if (error || !data) {
      return <Text>{error?.message || 'An unexpected error occurred.'}</Text>;
    }

    return (
      <>
        <div className={c('grid')}>
          {data.data.map((camera) => {
            const stream = getStreamForCamera(camera.id);

            return (
              <article
                className={c('grid-item')}
                key={camera.id}
                onClick={(event) => handleGridItemNavigate(event, camera)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigateToCamera(camera, event.metaKey);
                  }
                }}
                tabIndex={0}
                role="button"
              >
                <VideoSourceSnapshot
                  className={c('grid-item-preview')}
                  source={stream || camera}
                  camera={camera}
                  autoRefresh={false}
                  showSettingsButton={hasAccess('deploy_edit')}
                  onSettingsClick={() =>
                    navigate(
                      `/applications/${applicationID}/deploy/cameras/${camera.id}`,
                      {
                        state: { tab: 'settings' },
                      }
                    )
                  }
                />
              </article>
            );
          })}
        </div>

        {data.total_pages > 1 && (
          <div className={c('pagination')}>
            <Pagination
              total={data.total_pages}
              value={props.page ?? 1}
              onChange={props.onPageChange}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <PaginatedTable
      {...props}
      id="cameras-table"
      label="cameras"
      columns={columns}
      bulkActions={bulkActions}
      onRowNavigate={({ id }) => `/deploy/cameras/${id}`}
      emptyMessage={emptyMessage}
    />
  );
}

const Memoized = React.memo(CamerasTable);

export { Memoized as CamerasTable };
